import { useCallback, useEffect, useMemo } from 'react';

import { css } from '@compiled/react';
import { flattenValues } from '@uniformdev/canvas';
import { defineMessages, useIntl } from 'react-intl';
import { FilterDefinition } from 'src/uniform/contenttypes/filterDefinition';

import { media } from '@atlaskit/primitives/responsive';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { Tracking } from '@/analytics/atl-analytics/tracking';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import { useFireFilterClickAnalytics } from '../../filterable-gallery/src/hooks/use-fire-filter-click-analytics';
import {
	applyFilters,
	applySearch,
	buildLabelMaps,
	coerceToPrimitiveArray,
	resolveFieldPath,
} from '../../filterable-gallery/src/utils/filterEngine';

import { CareersSearchField } from './components/careers-search-field';
import { FacetPanel } from './components/facet-panel';
import { JobTable } from './components/job-table';
import { ResultsCount } from './components/results-count';
import { useCareersFilterState } from './hooks/use-careers-filter-state';
import { countFacetOptions } from './utils/count-options';
import { groupItemsByFieldPath } from './utils/group-items';
import { formatLocationSummary } from './utils/icims-location';

import type { CareersDataItem, FacetGroup, FacetOption, JobGroup, JobRow } from './types';
import type { SearchConfiguration } from '../../filterable-gallery/src/utils/filterEngine';

/** Bound Uniform values arrive wrapped as `{ value }`; authored ones arrive bare. */
type UniformValueParameter<T> = T | { value?: T };

type UniformBlockParameter = unknown;

interface FacetDataSourceEntry {
	fields?: {
		sourceId?: { value?: unknown };
		data?: { value?: unknown };
	};
}

const MISSING_VALUE_PLACEHOLDER = '—';

/**
 * This gallery renders the `icimsJob` Contentful model and nothing else, so the
 * field paths are fixed in code rather than authored per composition. They were
 * `titlePath`, `locationPath`, `groupByPath` and `titleHrefPath` parameters. Read
 * the note in `configuration/components/careersGallery.json` before adding them
 * back.
 */
const FIELD_PATH = {
	title: 'fields.title',
	locations: 'fields.locations',
	category: 'fields.category',
	id: 'fields.id',
} as const;

/**
 * The live listing links every row to `/company/careers/details/<id>`, keyed on
 * the iCIMS job id. Verified on 219 of 219 rows, 2026-08-06.
 */
const DETAILS_ROUTE_PREFIX = '/company/careers/details/';

const messages = defineMessages({
	emptyState: {
		id: 'careers-gallery.empty-state',
		defaultMessage: 'No results found.',
	},
	resultsCount: {
		id: 'careers-gallery.results-count',
		defaultMessage: '{count, plural, one {# result} other {# results}}',
	},
	positionColumn: {
		id: 'careers-gallery.position-column',
		defaultMessage: 'Position',
	},
	locationColumn: {
		id: 'careers-gallery.location-column',
		defaultMessage: 'Location',
	},
	searchPlaceholder: {
		id: 'careers-gallery.search-placeholder',
		defaultMessage: 'Search the results',
	},
});

export interface UniformCareersGalleryProps {
	readonly data?: CareersDataItem[] | { value?: CareersDataItem[] };
	readonly positionColumnLabel?: UniformValueParameter<string>;
	readonly locationColumnLabel?: UniformValueParameter<string>;
	readonly resultsCountLabel?: UniformValueParameter<string>;
	readonly emptyStateLabel?: UniformValueParameter<string>;
	readonly searchPlaceholder?: UniformValueParameter<string>;
	readonly showSearchInput?: UniformValueParameter<boolean>;
	readonly urlSync?: UniformValueParameter<boolean>;
	readonly searchConfiguration?: UniformValueParameter<SearchConfiguration>;
	readonly filters?: UniformBlockParameter;
	readonly facetDataSources?: UniformBlockParameter;
}

const UniformCareersGallery = ({
	data,
	positionColumnLabel: rawPositionColumnLabel,
	locationColumnLabel: rawLocationColumnLabel,
	resultsCountLabel: rawResultsCountLabel,
	emptyStateLabel: rawEmptyStateLabel,
	searchPlaceholder: rawSearchPlaceholder,
	showSearchInput: rawShowSearchInput,
	urlSync: rawUrlSync,
	searchConfiguration: rawSearchConfiguration,
	filters: rawFilters,
	facetDataSources: rawFacetDataSources,
}: UniformCareersGalleryProps): JSX.Element => {
	const intl = useIntl();

	const showSearchInput = resolveBooleanParameter(rawShowSearchInput, true);
	const urlSync = resolveBooleanParameter(rawUrlSync, true);

	const positionColumnLabel =
		unwrapText(rawPositionColumnLabel) ?? intl.formatMessage(messages.positionColumn);
	const locationColumnLabel =
		unwrapText(rawLocationColumnLabel) ?? intl.formatMessage(messages.locationColumn);
	const emptyStateLabel = unwrapText(rawEmptyStateLabel) ?? intl.formatMessage(messages.emptyState);
	const searchPlaceholder =
		unwrapText(rawSearchPlaceholder) ?? intl.formatMessage(messages.searchPlaceholder);

	const dataItems = useMemo<CareersDataItem[]>(() => toDataItems(data), [data]);

	const filterDefs = useMemo<FilterDefinition[]>(() => {
		if (!rawFilters) return [];
		const flat = flattenValues(rawFilters) as unknown as FilterDefinition[];
		return Array.isArray(flat) ? flat : [];
	}, [rawFilters]);

	const optionsDataSources = useMemo<Record<string, unknown[]>>(() => {
		if (!rawFacetDataSources) return {};
		const entries = getUniformBlockEntries(rawFacetDataSources);

		return Object.fromEntries(
			entries.flatMap((entry) => {
				const sourceId = entry.fields?.sourceId?.value;
				const sourceData = entry.fields?.data?.value;
				if (typeof sourceId !== 'string' || !Array.isArray(sourceData)) return [];
				return [[sourceId, sourceData]];
			}),
		);
	}, [rawFacetDataSources]);

	// Plan §5.6: `applySearch` returns the FULL item list when no search keys are
	// configured — silently, which is how GWP-12212 shipped a search box that
	// filtered nothing. Deriving a default from the bound field paths means
	// search works out of the box and stays tunable by config.
	const searchConfiguration = useMemo<SearchConfiguration | undefined>(() => {
		const authored = unwrapParameterValue(rawSearchConfiguration);
		if (authored && Array.isArray(authored.keys) && authored.keys.length > 0) return authored;

		const keys = [
			{ name: FIELD_PATH.title, weight: 1 },
			{ name: FIELD_PATH.locations, weight: 0.6 },
		];

		return { keys, ignoreLocation: true };
	}, [rawSearchConfiguration]);

	const { filterObject, searchQuery, toggleOption, setSearchQuery, commitSearchQuery } =
		useCareersFilterState({ items: dataItems, filterDefs, urlSync, optionsDataSources });

	const labelMaps = useMemo(
		() => buildLabelMaps(dataItems, filterDefs, optionsDataSources),
		[dataItems, filterDefs, optionsDataSources],
	);

	const optionCounts = useMemo(
		() => countFacetOptions(dataItems, filterDefs, filterObject),
		[dataItems, filterDefs, filterObject],
	);

	const facetGroups = useMemo<FacetGroup[]>(
		() =>
			filterDefs.map((filterDef) => {
				const subFilter = filterObject[filterDef.id] ?? {};
				const options: FacetOption[] = Object.keys(subFilter).map((value) => ({
					value,
					label: labelMaps[filterDef.id]?.[value] ?? value,
					count: optionCounts[filterDef.id]?.[value] ?? 0,
					checked: subFilter[value]?.committedActive ?? false,
				}));
				// The reference renders both facet lists alphabetically. Auto-derived
				// options already arrive sorted, but statically authored `options` keep
				// their authored order, so sort here to cover both paths.
				options.sort((a, b) => a.label.localeCompare(b.label));
				return { id: filterDef.id, label: filterDef.label ?? filterDef.id, options };
			}),
		[filterDefs, filterObject, labelMaps, optionCounts],
	);

	const matchingItems = useMemo(() => {
		const filtered = applyFilters(dataItems, filterDefs, filterObject, 'and');
		return applySearch(filtered, searchQuery, searchConfiguration);
	}, [dataItems, filterDefs, filterObject, searchQuery, searchConfiguration]);

	const jobGroups = useMemo<JobGroup[]>(
		() =>
			groupItemsByFieldPath(matchingItems, FIELD_PATH.category).map((group) => ({
				key: group.key,
				label: group.label,
				rows: group.items.map((item, index) => toJobRow(item, `${group.key}-${index}`)),
			})),
		[matchingItems],
	);

	const resultsCountText = useMemo(
		() => formatResultsCount(intl, unwrapText(rawResultsCountLabel), matchingItems.length),
		[intl, rawResultsCountLabel, matchingItems.length],
	);

	// `Tracking.autoTrackLinks/Buttons` bind a listener to every <a>/<button>
	// that exists at call time — they are not delegated. Job-row anchors mount
	// and remount on every filter, search and grouping change, so without this
	// re-arm they carry the right data-event-* props and still fire nothing.
	// Same pattern as filterable-gallery.tsx:403–407.
	useEffect(() => {
		Tracking.autoTrackLinks(true);
		Tracking.autoTrackButtons(true);
	}, [jobGroups]);

	const fireFilterClickAnalytics = useFireFilterClickAnalytics();

	const handleToggleOption = useCallback(
		(facetId: string, optionValue: string) => {
			fireFilterClickAnalytics(facetId);
			toggleOption(facetId, optionValue);
		},
		[fireFilterClickAnalytics, toggleOption],
	);

	return (
		<UFOSegment name="uniform-careers-gallery">
			<TypographyStyles>
				<div css={containerStyle}>
					{facetGroups.length > 0 ? (
						<FacetPanel groups={facetGroups} onToggleOption={handleToggleOption} />
					) : null}
					<ResultsCount text={resultsCountText} />
					{showSearchInput ? (
						<CareersSearchField
							value={searchQuery}
							placeholder={searchPlaceholder}
							onChange={setSearchQuery}
							onSubmit={commitSearchQuery}
						/>
					) : null}
					<JobTable
						groups={jobGroups}
						positionColumnLabel={positionColumnLabel}
						locationColumnLabel={locationColumnLabel}
						emptyStateLabel={emptyStateLabel}
					/>
				</div>
			</TypographyStyles>
		</UFOSegment>
	);
};

// Compiled CSS must stay local to the file that uses it — an imported `css(...)`
// object can render without its generated classes in gallery contexts (see the
// comment at filterable-card/src/filterable-card.tsx:113).
const containerStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: '24px',
	[media.above.sm]: {
		gap: '32px',
	},
});

/**
 * Builds one table row from one `icimsJob` item. Every field path is fixed — see
 * `FIELD_PATH`.
 *
 * A field that resolves to nothing renders a visible placeholder and warns once,
 * rather than an empty cell that reads as missing data (plan §5.5 rule 4). With
 * the paths fixed in code, such a warning now means the BOUND DATA lacks the
 * field, not that a composition is misconfigured.
 */
export function toJobRow(item: CareersDataItem, key: string): JobRow {
	const title = resolveText(item, FIELD_PATH.title);
	const location = resolveLocation(item, FIELD_PATH.locations);
	const jobId = resolveText(item, FIELD_PATH.id);

	if (title === undefined) {
		warnOnce('title', `[careersGallery] "${FIELD_PATH.title}" resolved to nothing.`);
	}
	if (location === undefined) {
		warnOnce('location', `[careersGallery] "${FIELD_PATH.locations}" resolved to nothing.`);
	}
	if (jobId === undefined) {
		warnOnce(
			'job-id',
			`[careersGallery] "${FIELD_PATH.id}" resolved to nothing, so the row renders as plain text with no link.`,
		);
	}

	return {
		key,
		title: title ?? MISSING_VALUE_PLACEHOLDER,
		location: location ?? MISSING_VALUE_PLACEHOLDER,
		...(jobId !== undefined ? { href: `${DETAILS_ROUTE_PREFIX}${jobId}` } : {}),
	};
}

/**
 * This addresses the WHOLE `fields.locations` array, not one element,
 * because the summary depends on every element — see `formatLocationSummary`.
 *
 * A path that does not resolve at all returns `undefined`, so the caller warns
 * and shows the placeholder. A path that resolves to an EMPTY array returns the
 * empty string, which renders a blank cell. The live listing leaves the cell
 * blank for the 25 of 220 jobs that hold no location, and a placeholder there
 * would read as missing data.
 */
function resolveLocation(item: CareersDataItem, path: string): string | undefined {
	const resolved = resolveFieldPath(item, path);
	if (resolved === undefined) return undefined;
	return formatLocationSummary(coerceToPrimitiveArray(resolved));
}

function resolveText(item: CareersDataItem, path: string): string | undefined {
	return coerceToPrimitiveArray(resolveFieldPath(item, path))[0];
}

/**
 * Uses the authored ICU label when it parses, else the localised default. An
 * unparseable authored label must not take the whole surface down.
 */
function formatResultsCount(
	intl: ReturnType<typeof useIntl>,
	authoredLabel: string | undefined,
	count: number,
): string {
	if (authoredLabel) {
		try {
			return intl.formatMessage(
				{ id: 'careers-gallery.results-count.authored', defaultMessage: authoredLabel },
				{ count },
			);
		} catch {
			warnOnce(
				`results-count:${authoredLabel}`,
				`[careersGallery] resultsCountLabel "${authoredLabel}" is not a valid ICU message — using the default.`,
			);
		}
	}
	return intl.formatMessage(messages.resultsCount, { count });
}

function toDataItems(data: unknown): CareersDataItem[] {
	const unwrapped = Array.isArray(data)
		? data
		: isRecord(data) && Array.isArray(data['value'])
			? data['value']
			: undefined;
	return unwrapped ?? [];
}

function getUniformBlockEntries(data: unknown): FacetDataSourceEntry[] {
	const entries = Array.isArray(data)
		? data
		: isRecord(data) && Array.isArray(data['value'])
			? data['value']
			: [];
	return entries.filter(isRecord);
}

function resolveBooleanParameter(
	parameter: UniformValueParameter<boolean> | undefined,
	defaultValue: boolean,
): boolean {
	if (typeof parameter === 'boolean') return parameter;
	if (isRecord(parameter) && typeof parameter['value'] === 'boolean') return parameter['value'];
	return defaultValue;
}

function unwrapText(parameter: UniformValueParameter<string> | undefined): string | undefined {
	const value = unwrapParameterValue(parameter);
	if (typeof value !== 'string') return undefined;
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
}

function unwrapParameterValue<T>(parameter: UniformValueParameter<T> | undefined): T | undefined {
	if (parameter === undefined) return undefined;
	if (typeof parameter !== 'object' || parameter === null) return parameter as T;
	if (Array.isArray(parameter)) return parameter as T;
	return (parameter as { value?: T }).value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

const warnedKeys = new Set<string>();

function warnOnce(key: string, message: string): void {
	if (warnedKeys.has(key)) return;
	warnedKeys.add(key);
	// eslint-disable-next-line no-console
	console.warn(message);
}

export default UniformCareersGallery;
